import React, { createContext, useState, useEffect } from 'react';
import api from '../config/api';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const response = await api.get('/api/auth/profile');
      setUser(response.data.user);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function register(email, password, fullName) {
    try {
      setError(null);
      const response = await api.post('/api/auth/register', {
        email,
        password,
        fullName,
      });
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  }

  async function login(email, password) {
    try {
      setError(null);
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  }

  async function logout() {
    try {
      setError(null);
      await api.post('/api/auth/logout', {});
      setUser(null);
    } catch (err) {
      const message = err.response?.data?.error || 'Logout failed';
      setError(message);
      throw new Error(message);
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      register,
      login,
      logout,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
